/**
 * /check-in/new?dropoff={n} or ?return={n} — start a check-in already linked
 * to job #n.
 *
 * - **dropoff**: where a scanned label lands when its device was released to
 *   the customer while the job waited for parts (scan target
 *   `start_dropoff`). Creating the tickets binds the drop-off to the job and
 *   clears its "with customer" marker server-side
 *   (docs/device-release-awaiting-parts.md).
 * - **return**: the "Return check-in" on a finished job's scan summary — the
 *   customer is back with a device from a closed-out job (warranty, a new
 *   fault). The same pre-fill the ticket page's Create Return uses.
 */

import React from 'react';
import { Alert, Box, CircularProgress, Container } from '@mui/material';

import { useWorkItemByNumber } from '../hooks/useWorkItemByNumber';
import { CheckInCreatePage } from './CheckInCreatePage';

export interface CheckInLinkedEntryPageProps {
  link: 'dropoff' | 'return';
  ticketNumber: string;
  onBack: () => void;
  onCreated: (sessionId: string) => void;
}

export const CheckInLinkedEntryPage: React.FC<CheckInLinkedEntryPageProps> = ({
  link,
  ticketNumber,
  onBack,
  onCreated,
}) => {
  const { item: workItem, error } = useWorkItemByNumber(ticketNumber);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!workItem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50dvh' }}>
        <CircularProgress />
      </Box>
    );
  }
  // Keyed so a different job remounts the page and its once-on-mount seed.
  return (
    <CheckInCreatePage
      key={`${link}:${workItem.guid}`}
      onBack={onBack}
      onCreated={onCreated}
      dropOffForWorkItem={link === 'dropoff' ? workItem : null}
      returnOfWorkItem={link === 'return' ? workItem : null}
    />
  );
};

export default CheckInLinkedEntryPage;
